import { ChevronLeft } from "lucide-react";
import StrikeZone from "../StrikeZone";
import { SprayChart } from "../SprayChart";
import { PlayerSelector } from "../PlayerSelector";
import PitchInput from "../PitchInput";
import { HitInput } from "../HitInput";
import { GamePitchCounter } from "../PitchCounter";
import PitchTable from "../../tables/PitchTable";

export const GameChartSection = ({
  onBack,
  pitcher,
  batter,
  catcher,
  umpire,
  pitches,
  currentPitch,
  currentHit,
  shouldResetPlot,
  isPitcherView,
  isStrikeZone,
  isBullpen,
  zoneType,
  onPlayerEdit,
  onPitchChange,
  onHitChange,
  onSubmitPitch,
  onResetPitch,
  onToggleView,
  onSetStrikeZoneView,
  onPlotPitch,
  onDeletePitch,
  onUpdatePitch,
}) => {
  const pitcherPitches = pitches.filter((p) => p.pitcher?.name === pitcher?.name);
  const strikeZonePitches = pitches.filter(
    (p) => !p.isHit && (!batter || p.batter?.name === batter?.name)
  );
  const sprayHits = pitches.filter((p) => p.isHit && p.batter?.name === batter?.name);

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex flex-col items-center mb-6">
      <div className="w-full mb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
        >
          <ChevronLeft size={20} />
          <span>Back to Charts</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full mb-6">
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <PlayerSelector
                type="Pitcher"
                player={pitcher}
                onEdit={() => onPlayerEdit("pitcher")}
                required
              />
              <PlayerSelector
                type="Batter"
                player={batter}
                onEdit={() => onPlayerEdit("batter")}
                required
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <PlayerSelector
                type="Catcher"
                player={catcher}
                onEdit={() => onPlayerEdit("catcher")}
              />
              <PlayerSelector
                type="Umpire"
                player={umpire}
                onEdit={() => onPlayerEdit("umpire")}
              />
            </div>
          </div>

          {isStrikeZone ? (
            <PitchInput
              currentPitch={currentPitch}
              onChange={onPitchChange}
              onSubmit={onSubmitPitch}
              onReset={onResetPitch}
              disabled={!pitcher || !batter}
              isBullpen={false}
            />
          ) : (
            <HitInput
              currentHit={currentHit}
              onChange={onHitChange}
              onReset={onResetPitch}
              disabled={!pitcher || !batter}
            />
          )}
        </div>

        <div className="flex items-center justify-center bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative">
          <div className="absolute top-4 left-1/2 -translate-x-1/2 inline-flex rounded-lg p-1 bg-gray-100 shadow-sm">
            <button
              onClick={() => onSetStrikeZoneView(true)}
              className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                isStrikeZone
                  ? "bg-white text-blue-600 shadow"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
              }`}
            >
              Strike Zone
            </button>
            <button
              onClick={() => onSetStrikeZoneView(false)}
              className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                !isStrikeZone
                  ? "bg-white text-blue-600 shadow"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
              }`}
            >
              Spray Chart
            </button>
          </div>
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={onToggleView}
              className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md shadow-sm
                border border-gray-200 text-sm font-medium text-gray-700
                hover:bg-gray-50 transition-colors"
            >
              Toggle View
            </button>
          </div>
          <GamePitchCounter pitches={pitcherPitches} currentPitcher={pitcher} />
          {isStrikeZone ? (
            <StrikeZone
              key={isPitcherView ? "pitcher" : "catcher"}
              onPlotPitch={onPlotPitch}
              pitches={strikeZonePitches}
              currentPitch={currentPitch}
              shouldReset={shouldResetPlot}
              isBullpen={false}
              isPitcherView={isPitcherView}
            />
          ) : (
            <SprayChart
              hits={sprayHits}
              onPlotHit={onPlotPitch}
              currentHit={currentHit}
              shouldReset={shouldResetPlot}
            />
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Pitch History</h2>
        </div>
        <div className="w-full max-w-full">
          <PitchTable
            pitches={pitches}
            onDeletePitch={onDeletePitch}
            onUpdatePitch={onUpdatePitch}
            showBatter={true}
            isBullpen={isBullpen}
            zoneType={zoneType}
          />
        </div>
      </div>
    </div>
  );
};
